import { Component } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Store, select } from '@ngrx/store';
import { Banner, AlertType, BannerType } from '../../model/alert.model';
import { map } from 'rxjs/operators';
import { UserActionService } from '../../services/user-action.service';
import { UserActionType } from '../../../shared/model/user-action.model';
import { CoreMainSlice } from '../../core.state';
import { selectBannerData } from '../../state/core.selectors';
import { hideBannerSOA } from '../../state/core.actions';
import { BannerData } from '../../state/core.reducer';
import { Observable } from 'rxjs';
import { UserLinkProviderService } from '../../../user/link-provider.service';
import { sendOutcomeStrings } from '../../constants/send-outcome.constants';

@Component({
    standalone: false,
    selector: 'mibi-banner-container',
    template: `
        <mibi-banner *ngIf="(banner$ | async) as banner"
            [banner]="banner"
            (mainAction)="onMainAction(banner)"
            (auxilliaryAction)="onAuxAction(banner)"
        ></mibi-banner>`
})
export class BannerContainerComponent {

    readonly banner$: Observable<Banner | null>;

    private readonly banners: Record<BannerType, Banner> = {
        defaultError: {
            message: 'Ein Fehler ist aufgetreten.',

            type: AlertType.ERROR,
            icon: 'error',
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }
        },
        defaultSuccess: {
            message: 'Operation erfolgreich durchgeführt.',

            type: AlertType.SUCCESS,
            icon: 'done',
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }
        },
        noAuthorizationOrActivation: {
            message: 'Nicht authorisiert oder nicht aktiviert. '
                + 'Wenn bereits registriert, überprüfen Sie bitte Ihre Email auf einen Aktivierungslink',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        sendCancel: {
            message: 'Es wurden keine Probendaten an das BfR gesendet',
            type: AlertType.WARNING,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        validationFailure: {
            message: 'Es gab einen Fehler beim Validieren.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        uploadFailure: {
            message: 'Es gab einen Fehler beim Importieren der Datei.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        invalidEmailFailure: {
            message: 'Bitte geben Sie auf der ersten Seite des Untersuchungsauftrags eine gültige Email-Adresse an.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        sendFailure: {
            // Used when no support phone is configured; the send effects
            // dispatch a custom banner carrying the number when there is one.
            message: sendOutcomeStrings.nothingSent,
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        sendSuccessNoCustomerCopy: {
            // The data did arrive, so this is a warning rather than an error:
            // there is nothing for the sender to send again.
            message: sendOutcomeStrings.noCustomerCopy,
            type: AlertType.WARNING,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        sendSuccess: {
            type: AlertType.SUCCESS,
            message: `<div style="text-align:center">
            Der Auftrag wurde an das BfR gesendet.<br>
            <strong>Bitte drucken Sie</strong><br>
            <strong style="font-size:18px;color:#FF0000;">das PDF-Dokument</strong><br>
            in Ihrem Mailanhang aus und legen Sie es Ihren Isolaten bei.
            </div>`,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        validationErrors: {
            message: 'Es gibt noch rot gekennzeichnete Fehler. Bitte vor dem Senden korrigieren.',
            type: AlertType.ERROR,
            auxilliaryAction: { ...this.userActionService.getConfigOfType(UserActionType.SEND), label: 'Nochmals Senden' },
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },

        autocorrections: {
            message: 'Es wurden Felder automatisch korrigiert. Bitte prüfen und nochmals senden.',
            type: AlertType.WARNING,
            auxilliaryAction: { ...this.userActionService.getConfigOfType(UserActionType.SEND), label: 'Nochmals Senden' },
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        wrongUploadDatatype: {
            type: AlertType.ERROR,
            message: 'Falscher Dateityp: Dateien müssen vom Typ .xlsx sein.',
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        wrongUploadFilesize: {
            type: AlertType.ERROR,
            message: 'Zu grosse Datei: Dateien müssen kleiner als 0.5 Mb sein.',
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        accountActivationSuccess: {
            message: 'Bestätigung der E-Mail-Adresse erfolgreich!',
            type: AlertType.SUCCESS,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        accountActivationFailure: {
            message: 'Bestätigung der E-Mail-Adresse fehlgeschlagen. Bitte kontaktieren Sie das MiBi-Portal-Team.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        adminAccountActivationSuccess: {
            message: 'Kontoaktivierung erfolgreich!',
            type: AlertType.SUCCESS,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        adminAccountActivationFailure: {
            message: 'Kontoaktivierung fehlgeschlagen.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        passwordChangeSuccess: {
            message: 'Bitte melden Sie sich mit Ihrem neuen Passwort an',
            type: AlertType.SUCCESS,
            auxilliaryAction: { ...this.userActionService.getNavigationConfig(this.userLinks.login), label: 'Zum Login' },
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        passwordChangeFailure: {
            message: `Fehler beim Zurücksetzen des Passworts. Bitte klicken Sie im Bereich "Anmelden" auf "Passwort vergessen?" und lassen Sie sich einen neuen Link zum Zurücksetzen des Passworts zuschicken.`,
            type: AlertType.ERROR,
            auxilliaryAction: { ...this.userActionService.getNavigationConfig(this.userLinks.recovery), label: 'Zum Passwort-Reset' },
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        loginFailure: {
            message: 'Es gab einen Fehler beim Einloggen. Bitte registrieren Sie sich oder, wenn Sie sich schon registriert haben, kontaktieren Sie das MiBi-Portal-Team.',
            type: AlertType.ERROR,
            auxilliaryAction: { ...this.userActionService.getNavigationConfig(this.userLinks.register), label: 'Zur Registrierung' },
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        registrationFailure: {
            message: 'Es gab einen Fehler beim Registrieren.  Bitte kontaktieren Sie das MiBi-Portal-Team.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }

        },
        loginUnauthorized: {
            message: 'Nicht authorisiert, bitte einloggen.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }
        },
        exportFailure: {
            message: 'Es gab einen Fehler beim Exportieren der Datei.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }
        },
        downloadFailure: {
            message: 'Es gab einen Fehler beim Herunterladen der Zomo-Plan Datei.',
            type: AlertType.ERROR,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }
        },
        tooManyDataRows: {
            message: 'Maximal 500 Datenzeilen können mittels eines Untersuchungsauftrags hochgeladen werden. Zusätzliche Zeilen wurden verworfen.',
            type: AlertType.WARNING,
            mainAction: { ...this.userActionService.getConfigOfType(UserActionType.DISMISS_BANNER) }
        }
    };

    constructor(
        private store$: Store<CoreMainSlice>,
        private userActionService: UserActionService,
        private userLinks: UserLinkProviderService,
        private sanitizer: DomSanitizer
    ) {
        this.banner$ = this.store$.pipe(
            select(selectBannerData),
            map(bannerState => this.getBanner(bannerState))
        );
    }

    onMainAction(banner: Banner) {
        banner.mainAction.onExecute();
        this.store$.dispatch(hideBannerSOA());
    }

    onAuxAction(banner: Banner) {
        if (banner.auxilliaryAction) {
            banner.auxilliaryAction.onExecute();
        }
        this.store$.dispatch(hideBannerSOA());
    }

    private getBanner(bannerState: BannerData): Banner | null {
        let banner: Banner;

        if (bannerState.predefined) {
            banner = this.banners[bannerState.predefined];
        } else if (bannerState.custom) {
            banner = bannerState.custom;
        } else {
            return null;
        }

        const oriMessage = banner.message;
        const safeMessage = this.sanitizer.bypassSecurityTrustHtml(oriMessage);
        banner = { ...banner, message: safeMessage as string };

        if (!banner.icon) {
            switch (banner.type) {
                case AlertType.ERROR:
                    return { ...banner, icon: 'error' };
                case AlertType.SUCCESS:
                    return { ...banner, icon: 'done' };
                case AlertType.WARNING:
                default:
                    return { ...banner, icon: 'warning' };
            }
        }

        return banner;
    }
}
